/* Source: /home/sidsolaiyappan/skooch_ai/shared/action_validator.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { clockLabel, formatDayDate } from "./action_outcomes.js";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      }
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    return { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
  })()
};
function knownAiActionTypes() {
  return ["create_task", "create_fixed_event", "repeat_task", "delete_task", "reschedule_task", "resize_event", "split_event", "move_occurrence", "skip_occurrence", "delete_all_tasks", "undo_calendar_delete", "add_rule", "set_gap", "rebalance_week", "explain_schedule", "accept_all_placements", "reoptimize_schedule", "propose_plan", "suggest_times", "replan"];
}
function _stripPunct(tok) {
  let _t = String((tok ? tok : ""));
  _t = _jac.poly.call(_t.split(","), "join", "");
  _t = _jac.poly.call(_t.split("."), "join", "");
  _t = _jac.poly.call(_t.split("!"), "join", "");
  _t = _jac.poly.call(_t.split("?"), "join", "");
  _t = _jac.poly.call(_t.split(";"), "join", "");
  _t = _jac.poly.call(_t.split("("), "join", "");
  _t = _jac.poly.call(_t.split(")"), "join", "");
  _t = _jac.poly.call(_t.split("'"), "join", "");
  return _t;
}
function _isAllDigits(s) {
  let _s = String((s ? s : ""));
  if ((_s.length === 0)) {
    return false;
  }
  let _ok = true;
  let _i = 0;
  while ((_i < _s.length)) {
    let _c = _s.at(_i);
    if (((_c < "0") || (_c > "9"))) {
      _ok = false;
    }
    _i = (_i + 1);
  }
  return _ok;
}
function _pad2(n) {
  if ((n < 10)) {
    return ("0" + String(n));
  }
  return String(n);
}
function _describeShiftAmount(mins) {
  if ((mins <= 0)) {
    return "a bit";
  }
  if ((_jac.int.mod(mins, 60) === 0)) {
    let _h = Math.floor((mins / 60));
    if ((_h === 1)) {
      return "an hour";
    }
    return (String(_h) + " hours");
  }
  return (String(mins) + " minutes");
}
function _parseTimeToken(tok) {
  let _none = {"kind": "", "hhmm": "", "hour": (-1)};
  let _t = String((tok ? tok : ""));
  if (!_t) {
    return _none;
  }
  if ((_t === "noon")) {
    return {"kind": "exact", "hhmm": "12:00", "hour": 12};
  }
  if ((_t === "midnight")) {
    return {"kind": "exact", "hhmm": "00:00", "hour": 0};
  }
  let _ampm = "";
  if (_t.endsWith("pm")) {
    _ampm = "pm";
    _t = _t.slice(0, (_t.length - 2));
  } else if (_t.endsWith("am")) {
    _ampm = "am";
    _t = _t.slice(0, (_t.length - 2));
  }
  if (!_t) {
    return _none;
  }
  let _h = (-1);
  let _m = 0;
  let _hadColon = false;
  if ((_t.indexOf(":") >= 0)) {
    _hadColon = true;
    let _parts = _t.split(":");
    if ((((_parts.length !== 2) || !_isAllDigits(_parts[0])) || !_isAllDigits(_parts[1]))) {
      return _none;
    }
    _h = parseInt(_parts[0]);
    _m = parseInt(_parts[1]);
  } else {
    if (!_isAllDigits(_t)) {
      return _none;
    }
    _h = parseInt(_t);
  }
  if (((((((_h !== _h) || (_m !== _m)) || (_h < 0)) || (_h > 23)) || (_m < 0)) || (_m > 59))) {
    return _none;
  }
  if (((_ampm === "pm") && (_h < 12))) {
    _h = (_h + 12);
  }
  if (((_ampm === "am") && (_h === 12))) {
    _h = 0;
  }
  if ((((_ampm || _hadColon) || (_h >= 13)) || (_h === 0))) {
    return {"kind": "exact", "hhmm": ((_pad2(_h) + ":") + _pad2(_m)), "hour": _h};
  }
  return {"kind": "bare", "hhmm": "", "hour": _h};
}
function _monthIndexFromWord(word) {
  let _w = String((word ? word : ""));
  let _months = ["january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december"];
  let _i = 0;
  while ((_i < 12)) {
    let _m = _months.at(_i);
    if (((_w === _m) || ((_w.length === 3) && (_m.indexOf(_w) === 0)))) {
      return (_i + 1);
    }
    _i = (_i + 1);
  }
  return 0;
}
function _isOrdinalDayToken(tok) {
  let _t = String((tok ? tok : ""));
  if ((_t.length < 3)) {
    return false;
  }
  let _suf = _t.slice((_t.length - 2));
  if (((((_suf !== "st") && (_suf !== "nd")) && (_suf !== "rd")) && (_suf !== "th"))) {
    return false;
  }
  let _num = _t.slice(0, (_t.length - 2));
  if (!_isAllDigits(_num)) {
    return false;
  }
  let _n = parseInt(_num);
  return ((_n >= 1) && (_n <= 31));
}
function _isDayNumberToken(tok) {
  let _t = String((tok ? tok : ""));
  if (_isOrdinalDayToken(_t)) {
    return true;
  }
  if (!_isAllDigits(_t)) {
    return false;
  }
  let _n = parseInt(_t);
  return ((_n >= 1) && (_n <= 31));
}
function _isNumericDateToken(tok) {
  let _t = String((tok ? tok : ""));
  let _dash = _t.split("-");
  if ((_dash.length === 3)) {
    let _allD = true;
    for (const _p of _dash) {
      if (!_isAllDigits(_p)) {
        _allD = false;
      }
    }
    if (_allD) {
      return true;
    }
  }
  let _slash = _t.split("/");
  if (((_slash.length === 2) || (_slash.length === 3))) {
    let _allS = true;
    for (const _q of _slash) {
      if (!_isAllDigits(_q)) {
        _allS = false;
      }
    }
    if (((_allS && (_slash.length >= 2)) && (_t.length >= 3))) {
      return true;
    }
  }
  return false;
}
function _weekdayIndexFromWord(word) {
  let _w = String((word ? word : ""));
  if (((_w.indexOf("monday") === 0) || (_w === "mon"))) {
    return 1;
  }
  if ((((_w.indexOf("tuesday") === 0) || (_w === "tue")) || (_w === "tues"))) {
    return 2;
  }
  if (((_w.indexOf("wednesday") === 0) || (_w === "wed"))) {
    return 3;
  }
  if ((((_w.indexOf("thursday") === 0) || (_w === "thu")) || (_w === "thurs"))) {
    return 4;
  }
  if (((_w.indexOf("friday") === 0) || (_w === "fri"))) {
    return 5;
  }
  if (((_w.indexOf("saturday") === 0) || (_w === "sat"))) {
    return 6;
  }
  if (((_w.indexOf("sunday") === 0) || (_w === "sun"))) {
    return 7;
  }
  return 0;
}
function extractUserConstraints(message) {
  let _msg = String((message ? message : "")).toLowerCase();
  _msg = _jac.poly.call(_jac.poly.call(_msg, "split", String.fromCharCode(10)), "join", " ");
  _msg = _jac.poly.call(_jac.poly.call(_msg, "split", String.fromCharCode(9)), "join", " ");
  let _rawToks = _jac.poly.call(_msg, "split", " ");
  let _toks = [];
  for (const _rt of _rawToks) {
    let _ct = _stripPunct(_rt);
    if (_ct) {
      _toks.push(_ct);
    }
  }
  let _res = {"hasTime": false, "timeHHMM": "", "bareHour": (-1), "daypart": "", "recurring": false, "recurDays": [], "dateWord": "", "dateWords": [], "explicitDate": false, "deadlineish": false};
  if ((_msg.indexOf("morning") >= 0)) {
    _res["daypart"] = "morning";
  } else if ((_msg.indexOf("afternoon") >= 0)) {
    _res["daypart"] = "afternoon";
  } else if ((((_msg.indexOf("evening") >= 0) || (_msg.indexOf("tonight") >= 0)) || (_msg.indexOf("night") >= 0))) {
    _res["daypart"] = "evening";
  }
  let _timeAnchors = ["at", "around", "by", "@", "til", "till", "until", "from"];
  let _i = 0;
  while ((_i < _toks.length)) {
    let _t = _toks.at(_i);
    let _prev = ((_i > 0) ? _toks.at((_i - 1)) : "");
    if (!_res["hasTime"]) {
      let _pt = _parseTimeToken(_t);
      let _anchored = _timeAnchors.includes(_prev);
      if (((_pt["kind"] === "exact") && (((((_anchored || (_t === "noon")) || (_t === "midnight")) || (_t.indexOf(":") >= 0)) || _t.endsWith("am")) || _t.endsWith("pm")))) {
        _res["hasTime"] = true;
        _res["timeHHMM"] = _pt["hhmm"];
      } else if (((_pt["kind"] === "bare") && _anchored)) {
        _res["hasTime"] = true;
        _res["bareHour"] = _pt["hour"];
      }
    }
    if (((_t === "every") || (_t === "each"))) {
      let _nxt = (((_i + 1) < _toks.length) ? _toks.at((_i + 1)) : "");
      let _nxt2 = (((_i + 2) < _toks.length) ? _toks.at((_i + 2)) : "");
      if ((_nxt === "other")) {
        _nxt = _nxt2;
      }
      let _wd = _weekdayIndexFromWord(_nxt);
      if ((_wd > 0)) {
        _res["recurring"] = true;
        if (!_res["recurDays"].includes(_wd)) {
          _res["recurDays"] = _res["recurDays"].concat([_wd]);
        }
      } else if ((((((_nxt === "day") || (_nxt === "evening")) || (_nxt === "morning")) || (_nxt === "afternoon")) || (_nxt === "night"))) {
        _res["recurring"] = true;
        _res["recurDays"] = [1, 2, 3, 4, 5, 6, 7];
      } else if (((_nxt.indexOf("weekday") === 0) || (_nxt.indexOf("weeknight") === 0))) {
        _res["recurring"] = true;
        _res["recurDays"] = [1, 2, 3, 4, 5];
      } else if ((_nxt.indexOf("weekend") === 0)) {
        _res["recurring"] = true;
        _res["recurDays"] = [6, 7];
      } else if (((_nxt === "week") || (_nxt === "month"))) {
        _res["recurring"] = true;
      }
    }
    if ((((_t === "daily") || (_t === "nightly")) || (_t === "everyday"))) {
      _res["recurring"] = true;
      _res["recurDays"] = [1, 2, 3, 4, 5, 6, 7];
    }
    if ((_t === "weekly")) {
      _res["recurring"] = true;
    }
    if (_t.endsWith("s")) {
      let _wdp = _weekdayIndexFromWord(_t.slice(0, (_t.length - 1)));
      if ((_wdp > 0)) {
        _res["recurring"] = true;
        if (!_res["recurDays"].includes(_wdp)) {
          _res["recurDays"] = _res["recurDays"].concat([_wdp]);
        }
      }
    }
    let _isDateWord = false;
    if (((_t === "today") || (_t === "tomorrow"))) {
      _isDateWord = true;
    } else {
      let _wdd = _weekdayIndexFromWord(_t);
      if ((((_wdd > 0) && (_prev !== "every")) && (_prev !== "each"))) {
        _isDateWord = true;
      }
    }
    if (_isDateWord) {
      if (!_res["dateWord"]) {
        _res["dateWord"] = _t;
      }
      if (!_res["dateWords"].includes(_t)) {
        _res["dateWords"] = _res["dateWords"].concat([_t]);
      }
    }
    if (!_res["explicitDate"]) {
      if ((_isOrdinalDayToken(_t) || _isNumericDateToken(_t))) {
        _res["explicitDate"] = true;
      } else if ((_monthIndexFromWord(_t) > 0)) {
        let _nxtTok = (((_i + 1) < _toks.length) ? _toks.at((_i + 1)) : "");
        if ((_isDayNumberToken(_nxtTok) || _isDayNumberToken(_prev))) {
          _res["explicitDate"] = true;
        }
      }
    }
    if (((((_t === "by") || (_t === "before")) || (_t === "due")) || (_t === "deadline"))) {
      _res["deadlineish"] = true;
    }
    _i = (_i + 1);
  }
  return _res;
}
function resolveExplicitTime(constraints, llmWindow) {
  if (!_jac.builtin.bool(constraints)) {
    constraints = {};
  }
  let _exact = (constraints.timeHHMM ? String(constraints.timeHHMM) : "");
  if (_exact) {
    return {"time": _exact, "assumed": false, "alt": ""};
  }
  let _h = (((constraints.bareHour !== undefined) && (constraints.bareHour !== null)) ? constraints.bareHour : (-1));
  if (((_h < 1) || (_h > 12))) {
    return {"time": "", "assumed": false, "alt": ""};
  }
  let _dp = (constraints.daypart ? String(constraints.daypart) : "");
  let _win = (_dp ? _dp : String((llmWindow ? llmWindow : "")).toLowerCase());
  let _amH = ((_h < 12) ? _h : 12);
  let _pmH = ((_h < 12) ? (_h + 12) : 12);
  if ((_win === "morning")) {
    return {"time": (_pad2(_amH) + ":00"), "assumed": (_dp ? false : true), "alt": (!_dp ? (_pad2(_pmH) + ":00") : "")};
  }
  if (((_win === "afternoon") || (_win === "evening"))) {
    return {"time": (_pad2(_pmH) + ":00"), "assumed": (_dp ? false : true), "alt": (!_dp ? (_pad2(_amH) + ":00") : "")};
  }
  if ((_h === 12)) {
    return {"time": "12:00", "assumed": false, "alt": ""};
  }
  if (((_h >= 1) && (_h <= 6))) {
    return {"time": (_pad2(_pmH) + ":00"), "assumed": true, "alt": (_pad2(_amH) + ":00")};
  }
  return {"time": (_pad2(_amH) + ":00"), "assumed": true, "alt": (_pad2(_pmH) + ":00")};
}
function _isWindowName(s) {
  let _v = String((s ? s : "")).toLowerCase().trim();
  return (((_v === "morning") || (_v === "afternoon")) || (_v === "evening"));
}
function _isoAddDays(iso, n) {
  let _s = String((iso ? iso : ""));
  if ((_s.length < 10)) {
    return "";
  }
  let _dt = Reflect.construct(Date, [(_s.slice(0, 10) + "T00:00:00Z")]);
  let _ms = _dt.getTime();
  if ((_ms !== _ms)) {
    return "";
  }
  let _dt2 = Reflect.construct(Date, [(_ms + (n * 86400000))]);
  return _dt2.toISOString().slice(0, 10);
}
function supportedPinDates(constraints, weekIsoDays, todayIso) {
  if (!_jac.builtin.bool(constraints)) {
    constraints = {};
  }
  if (!_jac.builtin.bool(weekIsoDays)) {
    weekIsoDays = [];
  }
  let _out = [];
  let _today = String((todayIso ? todayIso : ""));
  let _words = (constraints.dateWords ? constraints.dateWords : []);
  for (const _w of _words) {
    let _wl = String(_w).toLowerCase();
    if ((_wl === "today")) {
      if ((_today && !_out.includes(_today))) {
        _out.push(_today);
      }
    } else if ((_wl === "tomorrow")) {
      let _tm = _isoAddDays(_today, 1);
      if ((_tm && !_out.includes(_tm))) {
        _out.push(_tm);
      }
    } else {
      let _wd = _weekdayIndexFromWord(_wl);
      if (((_wd > 0) && (weekIsoDays.length === 7))) {
        let _thisWk = String(weekIsoDays.at((_wd - 1)));
        let _nextWk = _isoAddDays(_thisWk, 7);
        if ((_thisWk && !_out.includes(_thisWk))) {
          _out.push(_thisWk);
        }
        if ((_nextWk && !_out.includes(_nextWk))) {
          _out.push(_nextWk);
        }
      }
    }
  }
  return _out;
}
function _clockToWindowName(hhmm) {
  let _s = String((hhmm ? hhmm : ""));
  if ((_s.indexOf(":") < 0)) {
    return "";
  }
  let _h = parseInt(_s.split(":")[0]);
  if ((_h !== _h)) {
    return "";
  }
  if ((_h < 12)) {
    return "morning";
  }
  if ((_h < 17)) {
    return "afternoon";
  }
  return "evening";
}
function _joinTitles(titles) {
  if (!_jac.builtin.bool(titles)) {
    titles = [];
  }
  if ((titles.length === 0)) {
    return "";
  }
  if ((titles.length === 1)) {
    return String(titles[0]);
  }
  let _head = titles.slice(0, (titles.length - 1));
  return ((_jac.poly.call(_head, "join", ", ") + " and ") + String(titles.at((titles.length - 1))));
}
function stripInventedPins(actions, constraints, weekIsoDays, todayIso) {
  if (!_jac.builtin.bool(actions)) {
    actions = [];
  }
  if (!_jac.builtin.bool(constraints)) {
    constraints = {};
  }
  if (!_jac.builtin.bool(weekIsoDays)) {
    weekIsoDays = [];
  }
  let _stripped = [];
  if (constraints.recurring) {
    return {"actions": actions, "strippedDateTitles": _stripped};
  }
  let _explicit = (constraints.explicitDate ? true : false);
  let _deadlineish = (constraints.deadlineish ? true : false);
  let _words = (constraints.dateWords ? constraints.dateWords : []);
  let _supported = supportedPinDates(constraints, weekIsoDays, todayIso);
  let _unresolvable = false;
  for (const _w of _words) {
    if (((_weekdayIndexFromWord(String(_w).toLowerCase()) > 0) && (weekIsoDays.length !== 7))) {
      _unresolvable = true;
    }
  }
  let _today = String((todayIso ? todayIso : ""));
  let _out = [];
  for (const _a of actions) {
    if ((!_a || (String(_a.type) !== "create_task"))) {
      _out.push(_a);
      continue;
    }
    let _pd = (_a.preferred_date ? String(_a.preferred_date).trim() : "");
    let _pt = (_a.preferred_time ? String(_a.preferred_time).trim() : "");
    let _dl = (_a.deadline ? String(_a.deadline).trim() : "");
    let _stripDate = false;
    if ((((_pd && (_pd.length >= 10)) && !_explicit) && !_unresolvable)) {
      let _ok = _supported.includes(_pd);
      if ((((!_ok && _deadlineish) && _today) && (_pd >= _today))) {
        for (const _sd of _supported) {
          if ((_pd <= String(_sd))) {
            _ok = true;
          }
        }
      }
      if (!_ok) {
        _stripDate = true;
      }
    }
    let _stripDeadline = false;
    if ((((_dl && (_dl.length >= 10)) && !_deadlineish) && !constraints.recurring)) {
      _stripDeadline = true;
    }
    let _demoteTime = "";
    if (((_pt && (_pt.indexOf(":") >= 0)) && !constraints.hasTime)) {
      let _dp = (constraints.daypart ? String(constraints.daypart) : "");
      _demoteTime = (_dp ? _dp : _clockToWindowName(_pt));
    }
    if (((!_stripDate && !_demoteTime) && !_stripDeadline)) {
      _out.push(_a);
      continue;
    }
    let _fixedA = _copyAction(_a);
    if (_stripDate) {
      _fixedA["preferred_date"] = "";
      _stripped.push((_a.title ? String(_a.title) : "untitled"));
    }
    if (_stripDeadline) {
      _fixedA["deadline"] = "";
    }
    if (_demoteTime) {
      _fixedA["preferred_time"] = _demoteTime;
    }
    _out.push(_fixedA);
  }
  return {"actions": _out, "strippedDateTitles": _stripped};
}
function _copyAction(action) {
  let _c = {};
  for (const _k of _jac.poly.call(Object, "keys", action)) {
    _c[_k] = action[_k];
  }
  return _c;
}
function validateAiActions(actions, sourceMessage, weekIsoDays, todayIso) {
  if (!_jac.builtin.bool(actions)) {
    actions = [];
  }
  if (!_jac.builtin.bool(weekIsoDays)) {
    weekIsoDays = [];
  }
  let _known = knownAiActionTypes();
  let _notes = [];
  let _refusals = [];
  let _kept = [];
  let _sawMalformed = false;
  for (const _a of actions) {
    if ((!_a || !_a.type)) {
      _sawMalformed = true;
      continue;
    }
    let _ty = String(_a.type);
    if (!_known.includes(_ty)) {
      _refusals.push((("The planner suggested an action I don't support (" + _ty) + "), so I skipped it to be safe."));
      continue;
    }
    if ((_ty === "replan")) {
      let _d = (_a.delta ? _a.delta : _a);
      let _hasRef = ((_a.task_title && String(_a.task_title).trim()) || (_a.task_ref && String(_a.task_ref).trim()));
      let _hasDelta = ((((_d.direction && String(_d.direction).trim()) || (_d.target_band && String(_d.target_band).trim())) || (_d.target_day && String(_d.target_day).trim())) || (_d.target_time && String(_d.target_time).trim()));
      if ((!_hasRef || !_hasDelta)) {
        _refusals.push("I couldn't tell which task to shift or in which direction \u2014 try 'move <task> earlier' or 'put <task> in the morning'.");
        continue;
      }
    }
    _kept.push(_a);
  }
  if (_sawMalformed) {
    _refusals.push("Part of the planner's reply was malformed, so I skipped it rather than guess.");
  }
  let _c = extractUserConstraints(sourceMessage);
  let _strip = stripInventedPins(_kept, _c, weekIsoDays, todayIso);
  _kept = _strip["actions"];
  if ((_strip["strippedDateTitles"].length > 0)) {
    let _stTitles = _strip["strippedDateTitles"];
    let _stPron = ((_stTitles.length > 1) ? "them" : "it");
    _notes.push((((("You didn't name a day for " + _joinTitles(_stTitles)) + ", so I left ") + _stPron) + " flexible \u2014 Skooch will scan the whole week for the best slot."));
  }
  if (_c["hasTime"]) {
    let _carried = false;
    let _taskIdxs = [];
    let _j = 0;
    while ((_j < _kept.length)) {
      let _ka = _kept.at(_j);
      let _kty = String(_ka.type);
      if ((_kty === "create_fixed_event")) {
        _carried = true;
      } else if ((_kty === "create_task")) {
        let _ptv = (_ka.preferred_time ? String(_ka.preferred_time).trim() : "");
        if ((_ptv && (_ptv.indexOf(":") >= 0))) {
          _carried = true;
        }
        _taskIdxs.push(_j);
      } else if (((_kty === "reschedule_task") || (_kty === "replan"))) {
        _carried = true;
      }
      _j = (_j + 1);
    }
    if ((!_carried && (_taskIdxs.length === 1))) {
      let _idx = _taskIdxs[0];
      let _orig = _kept.at(_idx);
      let _llmWin = ((_orig.preferred_time && _isWindowName(_orig.preferred_time)) ? String(_orig.preferred_time).trim() : "");
      let _r = resolveExplicitTime(_c, _llmWin);
      if (_r["time"]) {
        let _fixed = _copyAction(_orig);
        _fixed["preferred_time"] = _r["time"];
        _jac.poly.setitem(_kept, _idx, _fixed);
        let _ttl = (_orig.title ? String(_orig.title) : "it");
        if ((_r["assumed"] && _r["alt"])) {
          _notes.push((((((("I aimed " + _ttl) + " at ") + clockLabel(_r["time"])) + " \u2014 if you meant ") + clockLabel(_r["alt"])) + ", just say so."));
        } else {
          _notes.push((((("I aimed " + _ttl) + " at ") + clockLabel(_r["time"])) + ", the time you mentioned."));
        }
      }
    } else if ((!_carried && (_taskIdxs.length > 1))) {
      let _anyTimed = false;
      for (const _ti of _taskIdxs) {
        let _tv = (_kept.at(_ti).preferred_time ? String(_kept.at(_ti).preferred_time).trim() : "");
        if (_tv) {
          _anyTimed = true;
        }
      }
      if (!_anyTimed) {
        let _rm = resolveExplicitTime(_c, "");
        let _tLbl = (_rm["time"] ? clockLabel(_rm["time"]) : "the time you mentioned");
        _notes.push((("Heads up: you mentioned " + _tLbl) + ", but I couldn't pin it to one of these \u2014 tell me which one should be at that time."));
      }
    }
  }
  if (_c["recurring"]) {
    let _hasRecurringFixed = false;
    for (const _kf of _kept) {
      if (((_kf.type === "create_fixed_event") && _kf.is_recurring)) {
        _hasRecurringFixed = true;
      }
      if ((_kf.type === "repeat_task")) {
        _hasRecurringFixed = true;
      }
    }
    if (!_hasRecurringFixed) {
      let _taskCount = 0;
      let _datedInWeek = 0;
      let _trimmed = [];
      let _next = [];
      for (const _kk of _kept) {
        if ((_kk.type !== "create_task")) {
          _next.push(_kk);
          continue;
        }
        _taskCount = (_taskCount + 1);
        let _pd = (_kk.preferred_date ? String(_kk.preferred_date).trim() : "");
        let _outOfWeek = ((_pd && (weekIsoDays.length === 7)) && !weekIsoDays.includes(_pd));
        let _past = ((_pd && todayIso) && (_pd < String(todayIso)));
        if ((_outOfWeek || _past)) {
          _trimmed.push((_kk.title ? String(_kk.title) : "untitled"));
        } else {
          if (_pd) {
            _datedInWeek = (_datedInWeek + 1);
          }
          _next.push(_kk);
        }
      }
      _kept = _next;
      let _recurFixedHint = " (For a locked weekly slot instead, say 'make it a recurring event at a set time'.)";
      if ((_trimmed.length > 0)) {
        let _sunday = ((weekIsoDays.length === 7) ? formatDayDate(weekIsoDays[6]) : "Sunday");
        _notes.push(((((("Skooch plans one week at a time, so I kept the days through " + _sunday) + " and skipped ") + String(_trimmed.length)) + " beyond that \u2014 ask me to extend it when next week opens.") + _recurFixedHint));
      } else if ((_datedInWeek >= 2)) {
        _notes.push(("These cover the current week \u2014 Skooch plans one week at a time, so ask me to extend it when next week opens." + _recurFixedHint));
      } else if ((_taskCount >= 1)) {
        _notes.push(("True repeating tasks aren't supported yet, so this is a one-off." + _recurFixedHint));
      }
    }
  }
  return {"actions": _kept, "notes": _notes, "refusals": _refusals};
}
function translateReplanDelta(delta, currentPlacement, weekIsoDays) {
  if (!_jac.builtin.bool(delta)) {
    delta = {};
  }
  if (!_jac.builtin.bool(weekIsoDays)) {
    weekIsoDays = [];
  }
  let _none = {"ok": false, "error": "", "preferredDate": "", "preferredTime": "", "describe": ""};
  let _prefTime = "";
  let _prefDate = "";
  let _descBits = [];
  let _dir = (delta.direction ? String(delta.direction).toLowerCase().trim() : "");
  if (((_dir === "earlier") || (_dir === "later"))) {
    let _curStart = (_jac.poly.and_(currentPlacement, () => currentPlacement.startTime) ? String(currentPlacement.startTime) : "");
    if (_jac.builtin.bool(_jac.poly.and_((!_curStart && currentPlacement), () => currentPlacement.start_time))) {
      _curStart = String(currentPlacement.start_time);
    }
    if ((!_curStart || (_curStart.indexOf(":") < 0))) {
      let _pref = (_jac.poly.and_(currentPlacement, () => currentPlacement.preferredTime) ? String(currentPlacement.preferredTime).toLowerCase().trim() : "");
      if ((!_pref && _curStart)) {
        _pref = _curStart.toLowerCase().trim();
      }
      if ((_pref === "morning")) {
        _curStart = "09:00";
      } else if ((_pref === "afternoon")) {
        _curStart = "14:00";
      } else if ((_pref === "evening")) {
        _curStart = "19:00";
      } else if ((_pref && (_pref.indexOf(":") >= 0))) {
        _curStart = _pref;
      }
    }
    if ((!_curStart || (_curStart.indexOf(":") < 0))) {
      _none["error"] = "That task isn't on the calendar yet, so there's no current slot to shift from \u2014 want me to place it first?";
      return _none;
    }
    let _parts = _jac.poly.call(_curStart, "split", ":");
    let _mins = ((parseInt(_parts[0]) * 60) + parseInt(_parts[1]));
    if ((_mins !== _mins)) {
      _none["error"] = "I couldn't read that task's current slot \u2014 try giving me a concrete time instead.";
      return _none;
    }
    let _shift = 60;
    let _magRaw = (_jac.poly.and_(_jac.poly.and_(delta, () => (delta.minutes !== undefined)), () => (delta.minutes !== null)) ? parseInt(String(delta.minutes)) : 0);
    if (((_magRaw === _magRaw) && (_magRaw > 0))) {
      _shift = _magRaw;
    }
    if ((_shift < 5)) {
      _shift = 5;
    }
    if ((_shift > 720)) {
      _shift = 720;
    }
    _mins = ((_dir === "earlier") ? (_mins - _shift) : (_mins + _shift));
    if ((_mins < 420)) {
      _mins = 420;
    }
    if ((_mins > 1320)) {
      _mins = 1320;
    }
    let _hh = Math.floor((_mins / 60));
    let _mm = (_mins - (_hh * 60));
    _prefTime = ((_pad2(_hh) + ":") + _pad2(_mm));
    _descBits.push(((_describeShiftAmount(_shift) + " ") + _dir));
    let _curDate = "";
    if (_jac.builtin.bool(_jac.poly.and_(currentPlacement, () => currentPlacement.date))) {
      _curDate = String(currentPlacement.date);
    }
    if (_jac.builtin.bool(_jac.poly.and_((!_curDate && currentPlacement), () => currentPlacement.scheduled_date))) {
      _curDate = String(currentPlacement.scheduled_date);
    }
    if (_curDate) {
      _prefDate = _curDate;
    }
  }
  let _band = (delta.target_band ? String(delta.target_band).toLowerCase().trim() : "");
  if ((((_band === "morning") || (_band === "afternoon")) || (_band === "evening"))) {
    _prefTime = _band;
    _descBits.push(("to the " + _band));
  }
  let _tt = (delta.target_time ? String(delta.target_time).trim() : "");
  if ((_tt && (_tt.indexOf(":") >= 0))) {
    _prefTime = _tt;
    _descBits.push(("to " + clockLabel(_tt)));
  }
  let _td = (delta.target_day ? String(delta.target_day).trim() : "");
  if (_td) {
    let _tdl = _td.toLowerCase();
    if (((_td.length >= 10) && (_td.indexOf("-") === 4))) {
      _prefDate = _td;
      _descBits.push(("to " + formatDayDate(_td)));
    } else {
      let _wd = _weekdayIndexFromWord(_tdl);
      if (((_wd === 0) && _isAllDigits(_td))) {
        let _wdn = parseInt(_td);
        if (((_wdn >= 1) && (_wdn <= 7))) {
          _wd = _wdn;
        }
      }
      if (((_wd > 0) && (weekIsoDays.length === 7))) {
        _prefDate = String(weekIsoDays.at((_wd - 1)));
        _descBits.push(("to " + formatDayDate(_prefDate)));
      } else if ((_wd > 0)) {
        _descBits.push(("to " + _td));
      }
    }
  }
  if ((!_prefTime && !_prefDate)) {
    _none["error"] = "I can shift a task earlier/later, into a morning/afternoon/evening window, or onto a specific day \u2014 tell me which and I'll re-plan it.";
    return _none;
  }
  return {"ok": true, "error": "", "preferredDate": _prefDate, "preferredTime": _prefTime, "describe": _jac.poly.call(_descBits, "join", ", ")};
}
export {_clockToWindowName, _copyAction, _describeShiftAmount, _isAllDigits, _isDayNumberToken, _isNumericDateToken, _isOrdinalDayToken, _isWindowName, _isoAddDays, _joinTitles, _monthIndexFromWord, _pad2, _parseTimeToken, _stripPunct, _weekdayIndexFromWord, extractUserConstraints, knownAiActionTypes, resolveExplicitTime, stripInventedPins, supportedPinDates, translateReplanDelta, validateAiActions};
//# sourceMappingURL=action_validator.js.map
